import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthState } from '../types';
import { apiService } from '../services/api';

interface AuthContextType extends AuthState {
  login: (googleToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    session: null,
    loading: true,
  });

  console.log('AuthProvider - Current auth state:', authState);

  const login = async (googleToken: string): Promise<boolean> => {
    try {
      console.log('Starting login process...');
      setAuthState(prev => ({ ...prev, loading: true }));
      
      const response = await apiService.authenticateWithGoogle(googleToken);
      console.log('API response:', response);
      
      if (response.success && response.data) {
        const session = response.data;
        console.log('Login successful, updating state...');
        
        // Store session data
        localStorage.setItem('sessionId', session.sessionId);
        localStorage.setItem('user', JSON.stringify(session.user));
        
        // Update authentication state
        const newAuthState = {
          isAuthenticated: true,
          user: {
            id: session.user.id,
            email: session.user.email,
            name: session.user.name,
            status: session.user.status,
            isSuperAdmin: session.user.isSuperAdmin,
          },
          session: session,
          loading: false,
        };
        
        console.log('Setting new auth state:', newAuthState);
        setAuthState(newAuthState);
        
        return true;
      } else {
        console.error('Login failed:', response.error);
        setAuthState(prev => ({ ...prev, loading: false }));
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('sessionId');
      localStorage.removeItem('user');
      
      setAuthState({
        isAuthenticated: false,
        user: null,
        session: null,
        loading: false,
      });
    }
  };

  const checkSession = async (): Promise<void> => {
    try {
      console.log('checkSession - Starting session check...');
      const sessionId = localStorage.getItem('sessionId');
      const storedUser = localStorage.getItem('user');
      
      console.log('checkSession - sessionId:', sessionId);
      console.log('checkSession - storedUser:', storedUser);
      
      if (!sessionId || !storedUser) {
        console.log('checkSession - No session data found, setting unauthenticated');
        setAuthState(prev => ({ ...prev, loading: false }));
        return;
      }

      console.log('checkSession - Validating session with backend...');
      const response = await apiService.validateSession();
      console.log('checkSession - Validation response:', response);
      
      if (response.success && response.data) {
        const session = response.data;
        console.log('checkSession - Session valid, setting authenticated state');
        
        setAuthState({
          isAuthenticated: true,
          user: {
            id: session.user.id,
            email: session.user.email,
            name: session.user.name,
            status: session.user.status,
            isSuperAdmin: session.user.isSuperAdmin,
          },
          session: session,
          loading: false,
        });
      } else {
        console.log('checkSession - Session invalid, clearing storage');
        // Session invalid, clear storage
        localStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        
        setAuthState({
          isAuthenticated: false,
          user: null,
          session: null,
          loading: false,
        });
      }
    } catch (error) {
      console.error('checkSession - Session check error:', error);
      
      // Clear storage on error
      localStorage.removeItem('sessionId');
      localStorage.removeItem('user');
      
      setAuthState({
        isAuthenticated: false,
        user: null,
        session: null,
        loading: false,
      });
    }
  };

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    checkSession,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};